/**
 * AddGroupDialog Component
 * 
 * Dialog for creating a new account group.
 */

import React from "react";
import {
  Box,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Stack,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import DuplicateDetectionTextField from "../../../components/DuplicateDetectionTextField";
import { AccountGroup } from "../hooks/useAccountSettingsUIState";
import { UseAccountSettingsUIStateReturn } from "../hooks/useAccountSettingsUIState";
import { UseAccountSettingsHandlersReturn } from "../hooks/useAccountSettingsHandlers";

export interface AddGroupDialogProps {
  open: boolean;
  onClose: () => void;
  uiState: UseAccountSettingsUIStateReturn;
  handlers: UseAccountSettingsHandlersReturn;
  groups: AccountGroup[];
}

/**
 * Add group dialog component
 */
export default function AddGroupDialog({
  open,
  onClose,
  uiState,
  handlers,
  groups,
}: AddGroupDialogProps): React.ReactElement {
  const handleClose = (): void => {
    uiState.setAddDialogOpen(false);
    uiState.setIsAddingGroup(false);
    uiState.setGroupNameError("");
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <Typography variant="h6">Add Account Group</Typography>
          <IconButton onClick={handleClose} size="small">
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <DuplicateDetectionTextField
            label="Group Name"
            value={uiState.newGroupName}
            onChange={uiState.setNewGroupName}
            existingItems={groups
              .filter((g) => {
                // When creating, exclude the group being created from duplicate check
                if (uiState.isAddingGroup && uiState.newGroupName.trim()) {
                  return g.name.toLowerCase() !== uiState.newGroupName.trim().toLowerCase();
                }
                return true;
              })
              .map((g) => ({ name: g.name, id: g.id }))}
            itemType="group"
            onErrorChange={(error) => {
              // Don't update error if we're in the process of adding (to prevent false positives)
              if (!uiState.isAddingGroup) {
                uiState.setGroupNameError(error);
              }
            }}
            fullWidth
            required
            autoFocus
            placeholder="e.g., Cash, Accounts, Cards"
          />
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 2, pb: 2, pt: 1, gap: 1, display: "flex" }}>
        <Button 
          onClick={handleClose}
          variant="outlined"
          sx={{ flex: 1, py: 1.5 }}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handlers.handleAddGroup}
          disabled={!uiState.newGroupName.trim() || uiState.isAddingGroup || !!uiState.groupNameError}
          sx={{ flex: 1, py: 1.5 }}
        >
          {uiState.isAddingGroup ? "Adding..." : "Add"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
